import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Alert,
  CircularProgress,
  FormControlLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import { Add, Close, Delete } from '@mui/icons-material';
import { Coupon, getAllCoupons, saveCoupon, deleteCoupon } from '../Firebase/firebaseApi.ts';

interface CouponsAdminTabProps {
  isAdmin: boolean;
}

const CouponsAdminTab: React.FC<CouponsAdminTabProps> = ({ isAdmin }) => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Coupon form state
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState('');
  const [couponType, setCouponType] = useState<'percentage' | 'fixed'>('percentage');
  const [couponFriendlyName, setCouponFriendlyName] = useState('');
  const [couponExpirationDate, setCouponExpirationDate] = useState<Date | null>(null);
  const [couponNeedProof, setCouponNeedProof] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponSuccess, setCouponSuccess] = useState<string | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);

  const loadCoupons = async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const couponsData = await getAllCoupons();
      setCoupons(couponsData);
    } catch (err: any) {
      console.error('Error loading coupons:', err);
      setError(err.message || 'שגיאה בטעינת הקופונים');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoupons();
  }, [isAdmin]);

  const handleDeleteCoupon = (couponCode: string) => {
    setCouponToDelete(couponCode);
    setDeleteConfirmOpen(true);
    setDeleteError(null);
    setDeleteSuccess(null);
  };

  const handleConfirmDelete = async () => {
    if (!couponToDelete) return;

    setDeleteLoading(true);
    try {
      await deleteCoupon(couponToDelete);
      setDeleteSuccess('קופון נמחק בהצלחה!');
      await loadCoupons(); // Reload the list of coupons
      setTimeout(() => {
        setDeleteConfirmOpen(false);
        setCouponToDelete(null);
        setDeleteSuccess(null);
      }, 1500);
    } catch (err: any) {
      console.error('Error deleting coupon:', err);
      setDeleteError(err.message || 'שגיאה במחיקת הקופון');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setCouponToDelete(null);
    setDeleteError(null);
    setDeleteSuccess(null);
  };

  const handleCreateCoupon = async () => {
    if (!couponCode.trim() || !couponFriendlyName.trim() || !couponDiscount || !couponExpirationDate) {
      setCouponError('יש למלא את כל השדות הנדרשים');
      return;
    }
    
    const discount = parseFloat(couponDiscount);
    if (isNaN(discount) || discount <= 0) {
      setCouponError('הנחה חייבת להיות מספר חיובי');
      return;
    }
    
    if (couponType === 'percentage' && discount > 100) {
      setCouponError('אחוז הנחה לא יכול להיות יותר מ-100%');
      return;
    }
    
    setCouponLoading(true);
    setCouponError(null);
    setCouponSuccess(null);
    
    try {
      const expirationDateStr = couponExpirationDate.toISOString().split('T')[0];
      const newCoupon: Coupon = {
        code: couponCode.trim(),
        discount,
        type: couponType,
        friendlyName: couponFriendlyName.trim(),
        expirationDate: expirationDateStr,
        needProof: couponNeedProof
      };
      
      await saveCoupon(couponCode.trim(), newCoupon);
      setCouponSuccess('קופון נוצר בהצלחה!');
      
      // Reload coupons
      await loadCoupons();
      
      // Reset form and close dialog after a short delay
      setTimeout(() => {
        setCouponCode('');
        setCouponFriendlyName('');
        setCouponDiscount('');
        setCouponType('percentage');
        setCouponExpirationDate(null);
        setCouponNeedProof(false);
        setCouponSuccess(null);
        setCouponDialogOpen(false);
      }, 1500);
    } catch (err: any) {
      setCouponError(err.message || 'שגיאה ביצירת הקופון');
    } finally {
      setCouponLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          קופונים
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setCouponCode('');
            setCouponFriendlyName('');
            setCouponDiscount('');
            setCouponType('percentage');
            setCouponExpirationDate(null);
            setCouponNeedProof(false);
            setCouponError(null);
            setCouponSuccess(null);
            setCouponDialogOpen(true);
          }}
        >
          צור קופון
        </Button>
      </Box>
      
      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>שם</TableCell>
              <TableCell>קוד</TableCell>
              <TableCell>סוג הנחה</TableCell>
              <TableCell>ערך הנחה</TableCell>
              <TableCell>תאריך תפוגה</TableCell>
              <TableCell>נדרש אישור</TableCell>
              <TableCell>סטטוס</TableCell>
              <TableCell>פעולות</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {coupons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    אין קופונים במערכת
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              coupons.map((coupon) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const expirationDate = new Date(coupon.expirationDate);
                expirationDate.setHours(23, 59, 59, 999);
                const isExpired = expirationDate < today;
                
                return (
                  <TableRow key={coupon.code}>
                    <TableCell>{coupon.friendlyName}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        {coupon.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{coupon.type === 'percentage' ? 'אחוזים' : 'סכום קבוע'}</TableCell>
                    <TableCell>
                      {coupon.type === 'percentage' ? `${coupon.discount}%` : `${coupon.discount}₪`}
                    </TableCell>
                    <TableCell>{coupon.expirationDate}</TableCell>
                    <TableCell>
                      {coupon.needProof ? (
                        <Chip label="כן" color="warning" size="small" />
                      ) : (
                        <Chip label="לא" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {isExpired ? (
                        <Chip label="פג תוקף" color="error" size="small" />
                      ) : (
                        <Chip label="פעיל" color="success" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteCoupon(coupon.code)}
                      >
                        מחק
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
        sx={{ direction: 'rtl' }}
      >
        <DialogTitle>מחיקת קופון</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          {deleteSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {deleteSuccess}
            </Alert>
          )}
          {!deleteLoading && !deleteSuccess && (
            <Typography>
              האם אתה בטוח שברצונך למחוק את הקופון "<strong>{couponToDelete}</strong>"?
              פעולה זו אינה ניתנת לביטול.
            </Typography>
          )}
          {deleteLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleteLoading}>
            ביטול
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleteLoading ? 'מוחק...' : 'מחק'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={couponDialogOpen}
        onClose={() => setCouponDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ direction: 'rtl' }}
      >
        <DialogTitle>יצירת קופון חדש</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label="קוד קופון"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              fullWidth
              required
              variant="filled"
              helperText="קוד ייחודי לקופון (לדוגמה: student)"
              InputLabelProps={{
                sx: {
                  right: 12,
                  left: 'auto',
                  transformOrigin: 'top right',
                }
              }}
              sx={{ direction: 'rtl' }}
            />
            
            <TextField
              label="שם ידידותי"
              value={couponFriendlyName}
              onChange={(e) => setCouponFriendlyName(e.target.value)}
              fullWidth
              required
              variant="filled"
              helperText="שם שיוצג למשתמש (לדוגמה: אני סטודנט)"
              InputLabelProps={{
                sx: {
                  right: 12,
                  left: 'auto',
                  transformOrigin: 'top right',
                }
              }}
              sx={{ direction: 'rtl' }}
            />
            
            <FormControl fullWidth variant="filled" sx={{ direction: 'rtl' }}>
              <InputLabel sx={{ right: 12, left: 'auto', transformOrigin: 'top right' }}>סוג הנחה</InputLabel>
              <Select
                value={couponType}
                onChange={(e) => setCouponType(e.target.value as 'percentage' | 'fixed')}
                sx={{ direction: 'rtl', textAlign: 'right' }}
              >
                <MenuItem value="percentage">אחוזים</MenuItem>
                <MenuItem value="fixed">סכום קבוע</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label={couponType === 'percentage' ? 'אחוז הנחה' : 'סכום הנחה (₪)'}
              value={couponDiscount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                setCouponDiscount(value);
              }}
              fullWidth
              required
              variant="filled"
              type="number"
              helperText={couponType === 'percentage' ? 'אחוז הנחה (לדוגמה: 10 עבור 10%)' : 'סכום הנחה בשקלים'}
              InputLabelProps={{
                sx: {
                  right: 12,
                  left: 'auto',
                  transformOrigin: 'top right',
                }
              }}
              sx={{ direction: 'rtl' }}
            />
            
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
              <DatePicker
                label="תאריך תפוגה"
                value={couponExpirationDate}
                onChange={(newValue) => setCouponExpirationDate(newValue)}
                format="yyyy-MM-dd"
                minDate={new Date()}
                sx={{ direction: 'rtl', width: '100%' }}
              />
            </LocalizationProvider>
            
            <FormControlLabel
              control={
                <Switch
                  checked={couponNeedProof}
                  onChange={(e) => setCouponNeedProof(e.target.checked)}
                  color="primary"
                />
              }
              label="נדרש אישור (העלאת תמונה)"
              sx={{ direction: 'rtl' }}
            />
            
            {couponError && (
              <Alert severity="error" onClose={() => setCouponError(null)}>
                {couponError}
              </Alert>
            )}
            
            {couponSuccess && (
              <Alert severity="success" onClose={() => setCouponSuccess(null)}>
                {couponSuccess}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCouponDialogOpen(false)}>
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCoupon}
            disabled={couponLoading}
            startIcon={couponLoading ? <CircularProgress size={16} /> : null}
          >
            {couponLoading ? 'יוצר קופון...' : 'צור קופון'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CouponsAdminTab;